import { Box, Flex, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type * as stats from '@blockscout/stats-types';
import type { StatsIntervalIds } from 'types/client/stats';

import { useI18n } from 'lib/i18n/context';
import ChartIntervalSelect from 'ui/shared/chart/ChartIntervalSelect';
import FilterInput from 'ui/shared/filters/FilterInput';
import Select from 'ui/shared/select/Select';
import { STATS_INTERVALS } from 'ui/stats/constants';

import { STATS_FILTER_KEYS } from './i18n';

type Props = {
  sections?: Array<stats.LineChartSection>;
  currentSection: string;
  onSectionChange: (newSection: string) => void;
  interval: StatsIntervalIds;
  onIntervalChange: (newInterval: StatsIntervalIds) => void;
  onFilterInputChange: (q: string) => void;
  isLoading: boolean;
  initialFilterValue: string;
};

const StatsFilters = ({
  sections,
  currentSection,
  onSectionChange,
  interval,
  onIntervalChange,
  onFilterInputChange,
  isLoading,
  initialFilterValue,
}: Props) => {
  const { t } = useI18n();

  const sectionOptions = React.useMemo(() => {
    return [
      { value: 'all', label: t(STATS_FILTER_KEYS.allStats) },
      ...(sections || []).map((section) => ({ value: section.id, label: section.title })),
    ];
  }, [ sections, t ]);

  const intervalOptions = React.useMemo(() => {
    return Object.keys(STATS_INTERVALS).map((id) => ({
      value: id,
      label: STATS_INTERVALS[id as StatsIntervalIds].title,
    }));
  }, []);

  const selectBg = useColorModeValue('#FFFFFF', '#111111');
  const selectBorder = useColorModeValue('#E9ECEF', '#222222');
  const selectColor = useColorModeValue('#000000', '#FFFFFF');
  const separatorColor = useColorModeValue('#E9ECEF', '#222222');
  const borderTopColor = useColorModeValue('#E9ECEF', '#222222');

  const selectStyleProps = {
    h: '40px',
    fontWeight: 500,
    fontSize: '14px',
    bgColor: selectBg,
    border: '1px solid',
    borderColor: selectBorder,
    borderRadius: '8px',
    color: selectColor,
    _hover: { bgColor: selectBg, color: selectColor },
    _focus: { bgColor: selectBg, color: selectColor },
    _active: { bgColor: selectBg, color: selectColor },
  };

  return (
    <Flex
      direction={{ base: 'column', lg: 'row' }}
      gap={{ base: 3, lg: 0 }}
      alignItems={{ base: 'stretch', lg: 'center' }}
      justifyContent="space-between"
      borderTop="1px solid"
      borderTopColor={ borderTopColor }
      pt={ 6 }
    >
      { /* Mobile: section + interval side by side */ }
      <Flex display={{ base: 'flex', lg: 'none' }} gap="8px" alignItems="center">
        <Box flex={ 1 } minW={ 0 }>
          <Select
            options={ sectionOptions }
            name="stats_section_mobile"
            defaultValue={ currentSection }
            onChange={ onSectionChange }
            isLoading={ isLoading }
            matchWidth
            w="100%"
            { ...selectStyleProps }
          />
        </Box>
        <Box flex={ 1 } minW={ 0 }>
          <Select
            options={ intervalOptions }
            name="stats_interval_mobile"
            defaultValue={ interval }
            onChange={ onIntervalChange }
            isLoading={ isLoading }
            matchWidth
            w="100%"
            { ...selectStyleProps }
          />
        </Box>
      </Flex>

      { /* Desktop: section + separator + TagGroupSelect */ }
      <Flex display={{ base: 'none', lg: 'flex' }} alignItems="center" gap={ 3 } flexShrink={ 0 }>
        <Select
          options={ sectionOptions }
          name="stats_section"
          defaultValue={ currentSection }
          onChange={ onSectionChange }
          isLoading={ isLoading }
          matchWidth
          w="160px"
          { ...selectStyleProps }
        />
        <Box
          w="1px"
          h="20px"
          bg={ separatorColor }
          flexShrink={ 0 }
        />
        <Box flexShrink={ 0 }>
          <ChartIntervalSelect interval={ interval } onIntervalChange={ onIntervalChange } isLoading={ isLoading } selectTagSize="md"/>
        </Box>
      </Flex>

      <FilterInput
        key={ initialFilterValue }
        isLoading={ isLoading }
        onChange={ onFilterInputChange }
        placeholder={ t(STATS_FILTER_KEYS.placeholder) }
        initialValue={ initialFilterValue }
        size="sm"
        w={{ base: '100%', lg: '520px' }}
        minW={{ base: '100%', lg: '520px' }}
      />
    </Flex>
  );
};

export default StatsFilters;
